import { createContext, useState, useContext, useEffect, ReactNode, useRef } from 'react'
import { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/services/supabase'
import { Database } from '@/types/supabase'
import { AUTH_CONTEXT_INITIAL_STATE } from './auth-constants'
import { isSessionVerified, markSessionVerified, clearSessionVerified } from '@/utils/session-utils'

type Profile = Database['public']['Tables']['profiles']['Row']

// Define alert severity type instead of importing from MUI
type AlertColor = 'success' | 'info' | 'warning' | 'error'

interface AuthContextType {
  session: Session | null
  user: User | null
  profile: Profile | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
  success: string | null
  severity: AlertColor
  signOut: () => Promise<void>
  signIn: (data: { email: string; password: string }) => Promise<{ error: string | null }>
  signUp: (data: { email: string; password: string; username?: string }) => Promise<{
    error: string | null
  }>
  updateProfile: (profile: Partial<Profile>) => Promise<{ error: string | null }>
  refreshSession: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error: string | null }>
  updatePassword: (password: string) => Promise<{ error: string | null }>
}

const AuthContext = createContext<AuthContextType>(AUTH_CONTEXT_INITIAL_STATE)

export function useAuth() {
  return useContext(AuthContext)
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [severity, setSeverity] = useState<AlertColor>('info')

  // Track if we've already initialized auth
  const hasInitializedRef = useRef(false)

  // Utility function to fetch user profile
  const fetchProfile = async (userId: string): Promise<Profile | null> => {
    try {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single()

      if (error) {
        return null
      }

      return data as Profile
    } catch (error) {
      return null
    }
  }

  // Initialize auth state
  useEffect(() => {
    // Skip initialization if we've already done it
    if (hasInitializedRef.current) {
      return
    }

    const initializeAuth = async () => {
      try {
        setIsLoading(true)

        // Get the current session
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession()

        if (error) {
          setError(error.message)
        } else if (session) {
          setSession(session)
          setUser(session.user)
          setIsAuthenticated(true)

          // Store verification status in sessionStorage
          markSessionVerified()

          // Fetch user profile
          const profileData = await fetchProfile(session.user.id)
          setProfile(profileData)
        } else {
          // Clear verification flag
          clearSessionVerified()
        }
      } catch (error) {
        setError('Failed to initialize authentication')
      } finally {
        setIsLoading(false)
        hasInitializedRef.current = true
      }
    }

    initializeAuth()

    // Set up auth state change listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN') {
        setIsAuthenticated(true)
        // Store verification status in sessionStorage
        markSessionVerified()
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false)
        // Clear verification flag
        clearSessionVerified()
      } else if (event === 'TOKEN_REFRESHED') {
        // Ensure verification flag is set
        if (session) {
          markSessionVerified()
        }
      }

      setSession(session)
      setUser(session?.user || null)

      if (session?.user) {
        const profileData = await fetchProfile(session.user.id)
        setProfile(profileData)
      } else {
        setProfile(null)
      }
    })

    // Clean up subscription
    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Sign in function
  const signIn = async (data: { email: string; password: string }) => {
    try {
      setIsLoading(true)
      setError(null)
      setSuccess(null)

      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      })

      if (error) {
        setError(error.message)
        setSeverity('error')
        return { error: error.message }
      }

      setSuccess('Signed in successfully')
      setSeverity('success')
      return { error: null }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error during sign in'
      setError(errorMessage)
      setSeverity('error')
      return { error: errorMessage }
    } finally {
      setIsLoading(false)
    }
  }

  // Sign up function
  const signUp = async (data: { email: string; password: string; username?: string }) => {
    try {
      setIsLoading(true)
      setError(null)
      setSuccess(null)

      const { error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            username: data.username || data.email.split('@')[0],
          },
        },
      })

      if (error) {
        setError(error.message)
        setSeverity('error')
        return { error: error.message }
      }

      setSuccess('Please check your email to confirm your account')
      setSeverity('success')
      return { error: null }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error during sign up'
      setError(errorMessage)
      setSeverity('error')
      return { error: errorMessage }
    } finally {
      setIsLoading(false)
    }
  }

  // Sign out function
  const signOut = async () => {
    try {
      setIsLoading(true)
      setError(null)
      setSuccess(null)

      // Clear profile data first
      setProfile(null)

      // Clear any cached avatar images by adding fake URL params with timestamps
      if (profile?.avatar_url) {
        // Create a temporary image to force a reload of the avatar when logging back in
        const img = new Image()
        img.src = `${profile.avatar_url}?clear=${Date.now()}`
      }

      // Sign out from Supabase
      const { error } = await supabase.auth.signOut()

      if (error) {
        setError(error.message)
        setSeverity('error')
        return
      }

      // Clear session data
      setSession(null)
      setUser(null)
      setIsAuthenticated(false)

      // Clear session verification flag
      clearSessionVerified()

      // Set success message
      setSuccess('Signed out successfully')
      setSeverity('success')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error signing out'
      setError(errorMessage)
      setSeverity('error')
    } finally {
      setIsLoading(false)
    }
  }

  // Reset password function
  const resetPassword = async (email: string) => {
    try {
      setIsLoading(true)
      setError(null)
      setSuccess(null)

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) {
        setError(error.message)
        setSeverity('error')
        return { error: error.message }
      }

      setSuccess('Password reset email sent. Please check your inbox.')
      setSeverity('success')
      return { error: null }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error during password reset'
      setError(errorMessage)
      setSeverity('error')
      return { error: errorMessage }
    } finally {
      setIsLoading(false)
    }
  }

  // Update password function
  const updatePassword = async (password: string) => {
    try {
      setIsLoading(true)
      setError(null)
      setSuccess(null)

      const { error } = await supabase.auth.updateUser({
        password,
      })

      if (error) {
        setError(error.message)
        setSeverity('error')
        return { error: error.message }
      }

      setSuccess('Password updated successfully')
      setSeverity('success')
      return { error: null }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error updating password'
      setError(errorMessage)
      setSeverity('error')
      return { error: errorMessage }
    } finally {
      setIsLoading(false)
    }
  }

  // Update profile function
  const updateProfile = async (profileData: Partial<Profile>) => {
    try {
      setIsLoading(true)
      setError(null)
      setSuccess(null)

      if (!user) {
        const errorMessage = 'You must be logged in to update your profile'
        setError(errorMessage)
        setSeverity('error')
        return { error: errorMessage }
      }

      // Update the profile in Supabase
      const { error } = await supabase.from('profiles').update(profileData).eq('id', user.id)

      if (error) {
        setError(error.message)
        setSeverity('error')
        return { error: error.message }
      }

      // Get updated profile
      const updatedProfile = await fetchProfile(user.id)
      setProfile(updatedProfile)

      setSuccess('Profile updated successfully')
      setSeverity('success')
      return { error: null }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error updating profile'
      setError(errorMessage)
      setSeverity('error')
      return { error: errorMessage }
    } finally {
      setIsLoading(false)
    }
  }

  // Refresh session explicitly
  const refreshSession = async () => {
    try {
      setIsLoading(true)

      const { data, error } = await supabase.auth.getSession()

      if (error) {
        setError(error.message)
        return
      }

      if (data.session) {
        setSession(data.session)
        setUser(data.session.user)
        setIsAuthenticated(true)
        markSessionVerified()

        // Refresh profile data too
        const profileData = await fetchProfile(data.session.user.id)
        setProfile(profileData)
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error refreshing session'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // Auth context value
  const value = {
    session,
    user,
    profile,
    isLoading,
    isAuthenticated,
    error,
    success,
    severity,
    signOut,
    signIn,
    signUp,
    updateProfile,
    refreshSession,
    resetPassword,
    updatePassword,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
